import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Mail, ArrowRight, KeyRound } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { trackAuthStep } from '@/lib/authFunnel';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
}

// A typed code, not a link. Corporate mail scanners GET every link in an
// incoming message, and our reset tokens are single-use, so the scanner was
// burning the token before the person ever clicked. Measured 24/08/2026:
// 20 requests, 20 machine logins seconds later, zero password changes.
type Step = 'email' | 'code';

const ERROR_COPY: Record<string, string> = {
  too_many_codes: "You've asked for several codes today. Try again tomorrow, or reply to the email Sarah sent you and she'll sort it out by hand.",
  cooldown: 'A code is already on its way. Give it a minute before asking for another.',
  invalid_code: "That code didn't match. Check the six digits in the email and try again.",
  too_many_attempts: 'Too many tries. Wait an hour, or reply to Sarah and she will help you directly.',
  server_error: 'Something went wrong on our side. Please try again in a moment.',
};

export function ForgotPasswordModal({ isOpen, onClose, initialEmail = '' }: ForgotPasswordModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('email');

  // Funil antes da conta (23/09/2026): abriu, pediu código, código certo ou errado.
  useEffect(() => {
    if (isOpen) trackAuthStep('forgot', 'opened');
  }, [isOpen]);

  const isValidEmail = email.includes('@') && email.includes('.');
  const isValidCode = /^\d{6}$/.test(code);

  const callResetFn = async (action: 'send' | 'verify', extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke('password-reset-code', {
      body: { action, email: email.trim().toLowerCase(), ...extra },
    });
    if (error) throw error;
    return data as { success: boolean; error?: string; session?: { access_token: string; refresh_token: string } };
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail || loading) return;

    setLoading(true);
    try {
      const data = await callResetFn('send');

      if (!data?.success) {
        trackAuthStep('forgot', 'code_send_failed', { detail: data?.error || 'server_error' });
        toast.error(ERROR_COPY[data?.error || 'server_error'] || ERROR_COPY.server_error);
        return;
      }

      trackAuthStep('forgot', 'code_sent');
      setStep('code');
      setCode('');
    } catch (err) {
      logger.error('[ForgotPassword] Send failed:', err);
      trackAuthStep('forgot', 'code_send_failed', { detail: 'exception' });
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!isValidCode || loading) return;

    setLoading(true);
    try {
      const data = await callResetFn('verify', { code });

      if (!data?.success || !data.session) {
        trackAuthStep('forgot', 'code_bad', { detail: data?.error || 'invalid_code' });
        toast.error(ERROR_COPY[data?.error || 'invalid_code'] || ERROR_COPY.invalid_code);
        return;
      }

      // Hand the verified session to the client, then send them straight to
      // the existing set-a-password screen, which reads getSession().
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (sessionError) {
        logger.error('[ForgotPassword] setSession failed:', sessionError);
        toast.error('Something went wrong. Please try again.');
        return;
      }

      // Liga o sucesso à conta que acabou de entrar pelo código.
      void supabase.auth.getSession().then(({ data: sess }) =>
        trackAuthStep('forgot', 'code_ok', { userId: sess.session?.user?.id }),
      );
      handleClose();
      navigate('/reset-password', { replace: true });
    } catch (err) {
      logger.error('[ForgotPassword] Verify failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    void verifyCode();
  };

  // Submit the moment the sixth digit lands. On a phone with the keyboard up,
  // a centred modal puts the Continue button under the keyboard, and the
  // person has "no space to click below the code" (Cindee, 05/09/2026, three
  // codes requested, none completed). Typing six digits is the whole gesture.
  useEffect(() => {
    if (step === 'code' && isValidCode && !loading) {
      void verifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step]);

  const handleClose = () => {
    setEmail(initialEmail);
    setCode('');
    setStep('email');
    setLoading(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            // decoracao-ok: é o véu que escurece o fundo, e sem fade ele pisca
            // preto. Tocar nele fecha o modal, mas isso é atalho: o painel tem
            // botão de fechar próprio, e o painel nasce visível.
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal — nasce VISÍVEL. Este é o caminho de quem já não está
              conseguindo entrar; se o frame de animação não chega (janela sem
              foco entrega ~1 por segundo), ela abre "Forgot password" e não vê
              nada. Só o deslocamento e a escala animam. */}
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            // Top-anchored on phones so the input and button stay above the
            // keyboard; centred only from `sm` up. Scrolls inside itself when
            // the viewport is shorter than the content.
            className="fixed inset-x-4 top-[max(1rem,env(safe-area-inset-top))] sm:top-1/2 sm:-translate-y-1/2 z-50 max-w-md mx-auto max-h-[calc(100dvh-2rem)]"
          >
            <div className="bg-card rounded-3xl border border-border overflow-y-auto overscroll-contain max-h-[calc(100dvh-2rem)]">
              {/* Header */}
              <div className="relative px-6 pt-6 pb-4">
                <button
                  onClick={handleClose}
                  className="absolute right-4 top-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>

                <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                  {step === 'code'
                    ? <KeyRound className="w-6 h-6 text-foreground" />
                    : <Mail className="w-6 h-6 text-foreground" />}
                </div>

                <h2 className="text-xl font-semibold text-foreground mb-1">
                  {step === 'code' ? 'Enter your code' : 'Reset password'}
                </h2>
                <p className="text-[15px] leading-relaxed text-muted-foreground">
                  {step === 'code'
                    ? 'We sent a six digit code. Type it below and you can pick a new password.'
                    : 'Enter your email and we will send you a six digit code'
                  }
                </p>
              </div>

              {/* Content */}
              <div className="px-6 pb-6">
                <AnimatePresence mode="wait">
                  {step === 'code' ? (
                    <motion.form
                      key="code"
                      initial={{ y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      onSubmit={handleVerifyCode}
                      className="space-y-4"
                    >
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{6}"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        disabled={loading}
                        autoFocus={!('standalone' in navigator)}
                        aria-label="Six digit code"
                        className="w-full h-16 px-4 rounded-2xl bg-secondary border border-border text-foreground text-center text-3xl font-semibold tracking-[0.4em] placeholder:text-muted-foreground/40 placeholder:tracking-[0.4em] outline-none focus:border-ring focus:bg-secondary/80 transition-all disabled:opacity-50"
                      />

                      <p className="text-[15px] text-muted-foreground text-center">
                        Sent to <span className="text-foreground font-medium">{email}</span>
                      </p>

                      <button
                        type="submit"
                        disabled={!isValidCode || loading}
                        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          <>
                            Continue
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      {/* Cada pedido novo invalida o codigo anterior, e ate
                          21/09/2026 nada dizia isso: quem tinha varios emails
                          abertos digitava o mais velho e levava "codigo nao
                          confere" sem entender o porque. Medido: 27 dos 114
                          pedidos de 45 dias queimaram um codigo ainda vivo. */}
                      <p className="text-[15px] leading-relaxed text-muted-foreground text-center">
                        Asked more than once? Only the newest email works. Use the one that just arrived.
                      </p>

                      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <p className="text-[15px] leading-relaxed text-amber-700 dark:text-amber-200">
                          No code after five minutes? It is usually in your spam folder, or your account is under a different email address. Many people buy with one address and try to sign in with another, and nothing is sent to an address we don't recognise. Check the address on your purchase receipt and try that one.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => { setStep('email'); setCode(''); }}
                        disabled={loading}
                        className="min-h-[48px] w-full text-[15px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      >
                        Use a different email, or send a new code
                      </button>
                    </motion.form>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      onSubmit={handleSendCode}
                      className="space-y-4"
                    >
                      <div className="relative">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          disabled={loading}
                          autoFocus={!('standalone' in navigator)}
                          aria-label="Email address"
                          className="w-full h-14 px-4 rounded-2xl bg-secondary border border-border text-foreground placeholder:text-muted-foreground outline-none focus:border-ring focus:bg-secondary/80 transition-all disabled:opacity-50"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={!isValidEmail || loading}
                        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Send My Code
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => { if (isValidEmail) setStep('code'); }}
                        disabled={!isValidEmail || loading}
                        className="min-h-[48px] w-full text-[15px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                      >
                        I already have a code
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
